import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import type { AgentDecision } from '../lib/agentDecisions'
import { sortAgentDecisions } from '../lib/agentDecisions'

type UseAgentSuggestionsOptions = {
  userId?: string | null
  enabled?: boolean
}

export function useAgentSuggestions({ userId, enabled = true }: UseAgentSuggestionsOptions) {
  const [suggestions, setSuggestions] = useState<AgentDecision[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!userId || !enabled) {
      setSuggestions([])
      return
    }

    setIsLoading(true)
    setError(null)

    const { data, error: queryError } = await supabase
      .from('agent_decisions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })

    setIsLoading(false)

    if (queryError) {
      setError(queryError.message)
      return
    }

    setSuggestions(sortAgentDecisions((data ?? []) as AgentDecision[]))
  }, [userId, enabled])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!userId || !enabled) return

    const channel = supabase
      .channel(`agent-decisions-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'agent_decisions',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void load()
        },
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [userId, enabled, load])

  const accept = useCallback(
    async (decisionId: string) => {
      setSuggestions((prev) => prev.filter((d) => d.id !== decisionId))

      const { data, error: invokeError } = await supabase.functions.invoke<{
        success?: boolean
        error?: string
      }>('agent-apply', {
        body: { decision_id: decisionId, reviewed_by: 'user', action: 'apply' },
      })

      if (invokeError || data?.success === false) {
        await load()
        throw new Error(data?.error ?? invokeError?.message ?? 'apply_failed')
      }
    },
    [load],
  )

  const skip = useCallback(
    async (decisionId: string) => {
      setSuggestions((prev) => prev.filter((d) => d.id !== decisionId))

      const { error: updateError } = await supabase
        .from('agent_decisions')
        .update({ status: 'rejected', reviewed_by: 'user' })
        .eq('id', decisionId)
        .eq('status', 'pending')

      if (updateError) {
        await load()
        throw new Error(updateError.message)
      }
    },
    [load],
  )

  const sorted = useMemo(() => sortAgentDecisions(suggestions), [suggestions])

  return {
    suggestions: sorted,
    isLoading,
    error,
    accept,
    skip,
    reload: load,
  }
}
